import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { MapPin, Users, UserPlus, UserCheck, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { useMeta } from "@/lib/seo";
import EventCard from "@/components/EventCard";
import CrewEditDialog from "@/components/CrewEditDialog";

export default function CrewProfile() {
  const { crew_id } = useParams();
  const { t, user } = useApp();
  const [c, setC] = useState(null);
  const [tab, setTab] = useState("upcoming");
  const [editing, setEditing] = useState(false);
  const load = () => api.get(`/crews/${crew_id}`).then(r => setC(r.data)).catch(() => setC({ missing: true }));
  useEffect(() => { load(); }, [crew_id]); // eslint-disable-line react-hooks/exhaustive-deps
  useMeta({ title: c?.name ? `${c.name} · Soul Radar` : "Soul Radar", description: c?.name ? `${c.name} – Soul events${c.city ? ` in ${c.city}` : ""} on Soul Radar` : "", path: `/crews/${crew_id}` });
  if (!c) return null;
  if (c.missing) return <div className="max-w-3xl mx-auto p-8 eyebrow">{t("crewNotFound")}</div>;
  const follow = async (fn) => {
    if (!user) { toast.info(t("signInPitch")); return; }
    try { const { data } = await fn(); setC(x => ({ ...x, me_following: data.following, followers: data.followers })); } catch (err) { toast.error(err.response?.data?.detail || "Failed"); }
  };
  const list = tab === "upcoming" ? c.upcoming : c.past;
  const canEdit = user && (user.role === "admin" || c.me_role === "admin");
  return (
    <div className="max-w-5xl mx-auto px-4 md:px-6 py-8" data-testid="crew-profile">
      {editing && <CrewEditDialog crew={c} onClose={() => setEditing(false)} onSaved={d => setC(x => ({ ...x, ...d }))} />}
      <div className="border-b border-foreground pb-4 flex flex-wrap items-start justify-between gap-4">
        <div className="flex items-start gap-4">
          {c.logo && <img src={c.logo} alt="" className="w-20 h-20 sm:w-24 sm:h-24 object-contain border border-foreground bg-background shrink-0" data-testid="crew-logo" />}
          <div>
          <div className="eyebrow text-primary">{t("organizer")}</div>
          <h1 className="font-display uppercase text-4xl sm:text-5xl tracking-tighter leading-none mt-1" data-testid="crew-name">{c.name}</h1>
          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
            {(c.city || c.country) && <span className="inline-flex items-center gap-1" data-testid="crew-location"><MapPin className="w-3.5 h-3.5 text-primary" />{[c.city, c.country].filter(Boolean).join(", ")}</span>}
            <span className="inline-flex items-center gap-1"><Users className="w-3.5 h-3.5 text-primary" />{c.member_count} {t("crewMembers")}</span>
            <span data-testid="crew-followers"><b className="font-display text-base text-foreground">{c.followers}</b> {t("followers")}</span>
          </div>
          </div>
        </div>
        <div className="flex items-center gap-2" data-testid="crew-follow-controls">
          {canEdit && <button onClick={() => setEditing(true)} className="gg-btn !py-2 !px-3 text-xs" data-testid="crew-edit"><Pencil className="w-4 h-4" />{t("editCrew")}</button>}
          {c.me_following
            ? <button onClick={() => follow(() => api.delete(`/crews/${c.crew_id}/follow`))} className="gg-btn !py-2 !px-3 text-xs !bg-foreground !text-background" data-testid="crew-unfollow"><UserCheck className="w-4 h-4" />{t("following")}</button>
            : <button onClick={() => follow(() => api.post(`/crews/${c.crew_id}/follow`, { notify: true }))} className="gg-btn-primary !py-2 !px-3 text-xs" data-testid="crew-follow"><UserPlus className="w-4 h-4" />{t("follow")}</button>}
        </div>
      </div>
      {c.bio && <p className="mt-4 text-sm leading-relaxed whitespace-pre-line max-w-2xl" data-testid="crew-bio">{c.bio}</p>}
      {c.members?.length > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-2 text-sm" data-testid="crew-members">
          <span className="eyebrow !text-[10px] text-muted-foreground mr-1">{t("crewMembers")}</span>
          {c.members.map(m => <Link key={m.user_id} to={`/users/${m.user_id}`} className="gg-chip !py-1 !px-2 hover:bg-muted">{m.is_dj && m.dj_name ? m.dj_name : m.name}</Link>)}
        </div>
      )}
      <div className="mt-8 flex gap-1 border-b border-foreground" data-testid="crew-tabs">
        {["upcoming", "past"].map(k => <button key={k} onClick={() => setTab(k)} className={`px-3 py-2 font-display uppercase text-sm tracking-wide border-b-2 -mb-px ${tab === k ? "border-primary text-foreground" : "border-transparent text-muted-foreground hover:text-foreground"}`} data-testid={`crew-tab-${k}`}>{t(k === "upcoming" ? "upcomingEvents" : "pastEvents")} <span className="text-muted-foreground">({(k === "upcoming" ? c.upcoming : c.past).length})</span></button>)}
      </div>
      <div className="mt-4 grid grid-cols-1 xl:grid-cols-2 gap-3 md:gap-4" data-testid="crew-events">
        {list.map(e => <EventCard key={e.event_id} event={e} />)}
        {!list.length && <div className="text-sm text-muted-foreground py-6" data-testid="crew-empty">{t("noEventsYet")}</div>}
      </div>
    </div>
  );
}
